import React, { useEffect, useState, useRef } from "react";
import Lottie from "lottie-react";
import loader from "../assets/loader.json";
import Graph from "./Graph";
import { useUserListMutation } from "../redux/features/user/userApi";
import { DataGrid } from "@mui/x-data-grid";
import { FaEdit, FaUsers, FaIdCard, FaEnvelope, FaMobile, FaUniversity, FaShieldAlt, FaLock, FaUserCheck, FaUserTimes, FaChartBar, FaSignInAlt, FaBan } from "react-icons/fa";
import { BiExport } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { styles } from "../styles/style";
import { canRead, canWrite, getPermissionLevel, isAdmin } from "../utils/permissionUtils";

const Blocks = () => {
  const [userList, { isLoading, isSuccess, data: userData }] = useUserListMutation();
  const navigate = useNavigate();
  const [data, setData] = useState([]);

  // Permission checks
  const canReadUserDetails = canRead('user_details');
  const canWriteUserDetails = canWrite('user_details');
  const permissionLevel = getPermissionLevel('user_details');
  const [active, setActive] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [countryFilter, setCountryFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  const [allDates, setAllDates] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const targetElement = useRef();
  const userListRef = useRef();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");
  const [selectedAgeRange, setSelectedAgeRange] = useState("0-18");
  const [selectedGender, setSelectedGender] = useState("all");
  const [selectedLoginType, setSelectedLoginType] = useState("mobile");

  useEffect(() => {
    userList();
  }, []);
  useEffect(() => {
    if (isSuccess) {
      setData(userData.data);
    }
  }, [isSuccess]);

  // --- Data Processing for Dashboard Cards ---
  const users = data || [];
  // Country list for filter
  const countryList = ["All", ...Array.from(new Set(users.map(u => u.country).filter(Boolean)))];

  // Filtered users by country and date
  const filteredUsers = users.filter(u => {
    let countryMatch = countryFilter === "All" || u.country === countryFilter;
    let dateMatch = true;
    if (!allDates && dateFilter) {
      const regDate = new Date(u.createdAt).toISOString().slice(0, 10);
      dateMatch = regDate === dateFilter;
    }
    return countryMatch && dateMatch;
  });

  // 1. Total Users
  const totalUsers = filteredUsers.length;
  const activeUsers = filteredUsers.filter(u => u.isActive);
  const nonActiveUsers = filteredUsers.filter(u => !u.isActive);

  // 2. KYC
  const kycVerified = filteredUsers.filter(u => u.kycVerified);
  const kycNotVerified = filteredUsers.filter(u => !u.kycVerified);
  // const kycPending = filteredUsers.filter(u => u.kyc && (u.kyc.proofstatus === 2 || u.kyc.selfiestatus === 2 || u.kyc.panstatus === 2));
  const kycPending = filteredUsers.filter(u => u.kyc && (u.kyc.proofstatus === 2));
  // const kycReject = filteredUsers.filter(u => u.kyc && (u.kyc.proofstatus === 0 || u.kyc.selfiestatus === 0 || u.kyc.panstatus === 0)
  const kycReject = filteredUsers.filter(u => u.kyc && (u.kyc.proofstatus === 0)
  );

  // 3. Email Verification
  const emailVerified = filteredUsers.filter(u => u.emailVerified);
  const emailNotVerified = filteredUsers.filter(u => !u.emailVerified);

  // 4. Mobile Verification
  const mobileVerified = filteredUsers.filter(u => u.mobileVerified);
  const mobileNotVerified = filteredUsers.filter(u => !u.mobileVerified);

  // 5. Bank Verification
  const bankVerified = filteredUsers.filter(u => u.bankVerified);
  const bankNotVerified = filteredUsers.filter(u => !u.bankVerified);
  const newBankRequests = filteredUsers.filter(u => u.bankRequestStatus === 'new');

  // 6. TFA (Two Factor Auth) Example
  const tfaEnabled = filteredUsers.filter(u => u.tfaEnabled);
  const tfaDisabled = filteredUsers.filter(u => !u.tfaEnabled);


  const subscriptionActive = filteredUsers.filter(u => u.IsSubscribed);
  const subscriptionInactive = filteredUsers.filter(u => !u.IsSubscribed);

  // 7. Fund Passcode Example
  const fundPassEnabled = filteredUsers.filter(u => u.fundPassEnabled);
  const fundPassDisabled = filteredUsers.filter(u => !u.fundPassEnabled);

  // 8. Age/Gender Breakdown
  const usersByAge = (min, max) => filteredUsers.filter(u => u.age >= min && u.age <= max);
  const usersByGender = (arr, gender) => arr.filter(u => (u.gender || '').toLowerCase() === gender);
  // Example: 19-28 years
  const users0_18 = usersByAge(0, 18);
  const users0_18_male = usersByGender(users0_18, 'male');
  const users0_18_female = usersByGender(users0_18, 'female');
  const users19_28 = usersByAge(19, 28);
  const users19_28_male = usersByGender(users19_28, 'male');
  const users19_28_female = usersByGender(users19_28, 'female');
  const users29_39 = usersByAge(29, 39);
  const users29_39_male = usersByGender(users29_39, 'male');
  const users29_39_female = usersByGender(users29_39, 'female');
  const users40_50 = usersByAge(40, 50);
  const users40_50_male = usersByGender(users40_50, 'male');
  const users40_50_female = usersByGender(users40_50, 'female');
  const users51_60 = usersByAge(51, 60);
  const users51_60_male = usersByGender(users51_60, 'male');
  const users51_60_female = usersByGender(users51_60, 'female');
  const users61_70 = usersByAge(61, 70);
  const users61_70_male = usersByGender(users61_70, 'male');
  const users61_70_female = usersByGender(users61_70, 'female');
  const users71plus = filteredUsers.filter(u => u.age >= 71);
  const users71plus_male = usersByGender(users71plus, 'male');
  const users71plus_female = usersByGender(users71plus, 'female');

  // 9. Blocked Users (users that are inactive/blocked)
  const blockedUsers = filteredUsers.filter(u => u.isBlocked || !u.isActive);
  const blockedUsers_male = usersByGender(blockedUsers, 'male');
  const blockedUsers_female = usersByGender(blockedUsers, 'female');

  // 10. Logged in Users (Mobile)
  const loggedInUsers = filteredUsers.filter(u => u.isLoggedIn);
  const loggedInActive = loggedInUsers.filter(u => u.isActive);
  const loggedInInactive = loggedInUsers.filter(u => !u.isActive);

  // 11. Website Login Example
  const websiteLoggedInUsers = filteredUsers.filter(u => u.websiteLoggedIn);
  const websiteLoggedInActive = websiteLoggedInUsers.filter(u => u.isActive);
  const websiteLoggedInInactive = websiteLoggedInUsers.filter(u => !u.isActive);

  // --- End Data Processing ---

  // Get all years present in user data
  const years = Array.from(new Set(users.map(u => new Date(u.createdAt).getFullYear()))).sort((a, b) => b - a);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // Monthly data for selected year
  const monthlyData = Array(12).fill(0);
  users.forEach(u => {
    const date = new Date(u.createdAt);
    if (date.getFullYear() === selectedYear) {
      monthlyData[date.getMonth()] += 1;
    }
  });
  const monthlyGraphData = months.map((m, i) => ({ label: m, count: monthlyData[i] }));

  // Daily data for selected month/year
  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
  const dailyData = Array(daysInMonth).fill(0);
  users.forEach(u => {
    const date = new Date(u.createdAt);
    if (date.getFullYear() === selectedYear && date.getMonth() + 1 === selectedMonth) {
      dailyData[date.getDate() - 1] += 1;
    }
  });
  const dailyGraphData = Array.from({ length: daysInMonth }, (_, i) => ({ label: (i + 1).toString(), count: dailyData[i] }));

  const scrollToUserList = () => {
    userListRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCardClick = (cardType, filterType = null) => {
    setSelectedCard(cardType);
    setSelectedFilter(filterType);
    scrollToUserList();
  };

  // Table and search logic
  const filteredData = () => {
    let filtered = filteredUsers;

    console.log(`selectedCard----185-----,blocks------>`, selectedFilter);

    if (selectedCard === 'active') {
      if (selectedFilter === 'active') filtered = activeUsers;
      else if (selectedFilter === 'non-active') filtered = nonActiveUsers;
    }
    else if (selectedCard === 'kyc') {
      if (selectedFilter === 'verified') filtered = kycVerified;
      else if (selectedFilter === 'not-verified') filtered = kycNotVerified;
      else if (selectedFilter === 'pending') filtered = kycPending;
      else if (selectedFilter === 'reject') filtered = kycReject;
    }
    else if (selectedCard === 'email') {
      if (selectedFilter === 'verified') filtered = emailVerified;
      else if (selectedFilter === 'not-verified') filtered = emailNotVerified;
    }
    else if (selectedCard === 'mobile') {
      if (selectedFilter === 'verified') filtered = mobileVerified;
      else if (selectedFilter === 'not-verified') filtered = mobileNotVerified;
    }
    else if (selectedCard === 'bank') {
      if (selectedFilter === 'verified') filtered = bankVerified;
      else if (selectedFilter === 'not-verified') filtered = bankNotVerified;
      else if (selectedFilter === 'new-requests') filtered = newBankRequests;
    }
    else if (selectedCard === 'Subscription') {
      if (selectedFilter === 'enabled') filtered = subscriptionActive;
      else if (selectedFilter === 'disabled') filtered = subscriptionInactive;
    }
    else if (selectedCard === 'tfa') {
      if (selectedFilter === 'enabled') filtered = tfaEnabled;
      else if (selectedFilter === 'disabled') filtered = tfaDisabled;
    }
    else if (selectedCard === 'fund-pass') {
      if (selectedFilter === 'enabled') filtered = fundPassEnabled;
      else if (selectedFilter === 'disabled') filtered = fundPassDisabled;
    }
    else if (selectedCard === 'blocked') {
      filtered = blockedUsers;
      if (selectedFilter === 'male') filtered = blockedUsers_male;
      else if (selectedFilter === 'female') filtered = blockedUsers_female;
    }
    else if (selectedCard === 'logged-in') {
      filtered = loggedInUsers;
      if (selectedFilter === 'active') filtered = loggedInActive;
      else if (selectedFilter === 'non-active') filtered = loggedInInactive;
    }
    else if (selectedCard === 'website-logged-in') {
      filtered = websiteLoggedInUsers;
      if (selectedFilter === 'active') filtered = websiteLoggedInActive;
      else if (selectedFilter === 'non-active') filtered = websiteLoggedInInactive;
    }
    // Age/Gender breakdowns
    else if (selectedCard === '0-18') {
      filtered = users0_18;
      if (selectedFilter === 'male') filtered = users0_18_male;
      else if (selectedFilter === 'female') filtered = users0_18_female;
    }
    else if (selectedCard === '19-28') {
      filtered = users19_28;
      if (selectedFilter === 'male') filtered = users19_28_male;
      else if (selectedFilter === 'female') filtered = users19_28_female;
    }
    else if (selectedCard === '29-39') {
      filtered = users29_39;
      if (selectedFilter === 'male') filtered = users29_39_male;
      else if (selectedFilter === 'female') filtered = users29_39_female;
    }
    else if (selectedCard === '40-50') {
      filtered = users40_50;
      if (selectedFilter === 'male') filtered = users40_50_male;
      else if (selectedFilter === 'female') filtered = users40_50_female;
    }
    else if (selectedCard === '51-60') {
      filtered = users51_60;
      if (selectedFilter === 'male') filtered = users51_60_male;
      else if (selectedFilter === 'female') filtered = users51_60_female;
    }
    else if (selectedCard === '61-70') {
      filtered = users61_70;
      if (selectedFilter === 'male') filtered = users61_70_male;
      else if (selectedFilter === 'female') filtered = users61_70_female;
    }
    else if (selectedCard === '71+') {
      filtered = users71plus;
      if (selectedFilter === 'male') filtered = users71plus_male;
      else if (selectedFilter === 'female') filtered = users71plus_female;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.email?.toLowerCase().includes(query) ||
          user.username?.toLowerCase().includes(query) ||
          user.unique_id?.toLowerCase().includes(query)
      );
    }
    return filtered;
  };
  const newUserList = filteredData();

  // Table columns (add country, status, etc. as in image)
  const columns = [
    { field: "id", headerName: "S.No", flex: 0.5, minWidth: 80 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 150 },
    { field: "name", headerName: "Username", flex: 1, minWidth: 150 },
    { field: "unique_id", headerName: "UID", flex: 0.75, minWidth: 120 },
    {
      field: "kycVerified",
      headerName: "KYC status",
      flex: 0.75,
      minWidth: 100,
      renderCell: (params) => {
        let color = "";
        if (params.value === "Verified") color = "text-green-500 font-asemibold";
        else if (params.value === "Pending") color = "text-yellow-500 font-asemibold";
        else color = "text-red-600 font-asemibold";
        return <span className={color}>{params.value}</span>;
      }
    },
    { field: "created_at", headerName: "Register On", flex: 0.75, minWidth: 120 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => {
        const statusClass = params.value === "Active" ? "text-green-500" : "text-red-500";
        return <span className={statusClass}>{params.value}</span>;
      },
    },
    {
      field: "manage",
      headerName: "Manage",
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => {
        const id = params.row.userId;

        if (!canReadUserDetails) {
          return (
            <div className="flex items-center gap-2">
              <button
                disabled
                className="p-2 rounded-lg bg-gray-100 cursor-not-allowed opacity-50"
                title="No permission to view user details"
              >
                <FaEdit className="text-gray-400" size={16} />
              </button>
              <span className="text-xs text-red-500">🚫</span>
            </div>
          );
        }

        const isAdminUser = isAdmin();

        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/user/${id}`)}
              className="p-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors duration-200"
              title={`View user details (${isAdminUser ? 'Admin - Full Access' : permissionLevel + ' access'})`}
            >
              <FaEdit className="text-blue-500" size={16} />
            </button>
            {isAdminUser && (
              <span className="text-xs text-purple-500" title="Admin - Full Access"></span>
            )}
            {!isAdminUser && permissionLevel === 'read' && (
              <span className="text-xs text-yellow-500" title="Read Only"></span>
            )}
            {!isAdminUser && permissionLevel === 'write' && (
              <span className="text-xs text-green-500" title="Can Approve/Reject"></span>
            )}
          </div>
        );
      },
    },
  ];
  const rows = [];
  {
    newUserList &&
      newUserList.forEach((item, index) => {
        const options = { year: "numeric", month: "long", day: "numeric" };
        const date = new Date(item.createdAt).toLocaleDateString("en-US", options);
        const active = item.isActive ? "Active" : "Inactive";
        rows.push({
          id: index + 1,
          userId: item._id,
          email: item.email,
          name: item.username,
          unique_id: item.unique_id,
          kycVerified: item.kycVerified
            ? "Verified"
            : item.kyc && (item.kyc.proofstatus === 2)
              // : item.kyc && (item.kyc.proofstatus === 2 || item.kyc.selfiestatus === 2 || item.kyc.panstatus === 2)
              ? "Pending"
              // : item.kyc && (item.kyc.proofstatus === 0 || item.kyc.selfiestatus === 0 || item.kyc.panstatus === 0)
              : item.kyc && (item.kyc.proofstatus === 0)
                ? "Reject KYC"
                : "Not Verified",
          created_at: date,
          status: active,
        });
      });
  }

  // Export logic
  const convertToAoA = (data) => {
    const headers = Object.keys(data[0]);
    const rows = data.map((obj) => headers.map((header) => obj[header]));
    return [headers, ...rows];
  };
  const exportFile = () => {
    let filtered = newUserList;
    if (exportStartDate) {
      filtered = filtered.filter(u => new Date(u.created_at) >= new Date(exportStartDate));
    }
    if (exportEndDate) {
      filtered = filtered.filter(u => new Date(u.created_at) <= new Date(exportEndDate));
    }
    if (filtered.length === 0) {
      alert("No users found for the selected date range.");
      return;
    }
    const aoaData = convertToAoA(filtered);
    const worksheet = XLSX.utils.aoa_to_sheet(aoaData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "Users_List.xlsx");
  };

  const style = { height: 100, width: 100, margin: "auto", alignItem: "center" };

  // Card data configuration
  const cardConfigs = [
    {
      id: 'total-users',
      title: 'Total Users',
      icon: FaUsers,
      total: totalUsers,
      color: 'bg-gradient-to-r from-[#1e293b] to-blue-500',
      bgColor: 'bg-blue-50',
      items: [
        { label: 'Active', count: activeUsers.length, filter: 'active', color: 'bg-gr-100 hover:bg-green-200' },
        { label: 'Inactive', count: nonActiveUsers.length, filter: 'non-active', color: 'bg-red-100 hover:bg-red-200' }
      ]
    },
    {
      id: 'kyc',
      title: 'KYC Verification',
      icon: FaIdCard,
      total: totalUsers,
      color: 'bg-gradient-to-r from-[#1e293b] to-blue-500',
      bgColor: 'bg-blue-50',
      items: [
        { label: 'Verified', count: kycVerified.length, filter: 'verified', color: 'bg-gray-100 hover:bg-green-200' },
        { label: 'Not Verified', count: kycNotVerified.length, filter: 'not-verified', color: 'bg-red-100 hover:bg-gray-200' },
        { label: 'Pending', count: kycPending.length, filter: 'pending', color: 'bg-yellow-100 hover:bg-yellow-200' },
        { label: 'Rejected', count: kycReject.length, filter: 'reject', color: 'bg-red-100 hover:bg-red-200' }
      ]
    },
    {
      id: 'email',
      title: 'Email Verification',
      icon: FaEnvelope,
      total: totalUsers,
      color: 'bg-gradient-to-r from-[#1e293b] to-blue-500',
      bgColor: 'bg-blue-50',
      items: [
        { label: 'Verified', count: emailVerified.length, filter: 'verified', color: 'bg-gray-100 hover:bg-green-200' },
        { label: 'Not Verified', count: emailNotVerified.length, filter: 'not-verified', color: 'bg-red-100 hover:bg-red-200' }
      ]
    },
    // {
    //   id: 'mobile',
    //   title: 'Mobile Verification',
    //   icon: FaMobile,
    //   total: totalUsers,
    //   color: 'bg-gradient-to-r from-[#1e293b] to-blue-500',
    //   bgColor: 'bg-blue-50',
    //   items: [
    //     { label: 'Verified', count: mobileVerified.length, filter: 'verified', color: 'bg-gray-100 hover:bg-green-200' },
    //     { label: 'Not Verified', count: mobileNotVerified.length, filter: 'not-verified', color: 'bg-red-100 hover:bg-red-200' }
    //   ]
    // },
    // {
    //   id: 'bank',
    //   title: 'Bank Verification',
    //   icon: FaUniversity,
    //   total: totalUsers,
    //   color: 'bg-gradient-to-r from-[#1e293b] to-blue-500',
    //   bgColor: 'bg-blue-50',
    //   items: [
    //     { label: 'Verified', count: bankVerified.length, filter: 'verified', color: 'bg-gray-100 hover:bg-green-200' },
    //     { label: 'Not Verified', count: bankNotVerified.length, filter: 'not-verified', color: 'bg-red-100 hover:bg-red-200' },
    //     { label: 'New Requests', count: newBankRequests.length, filter: 'new-requests', color: 'bg-blue-100 hover:bg-blue-200' }
    //   ]
    // },
    {
      id: 'Subscription',
      title: 'Subscription',
      icon: FaShieldAlt,
      total: totalUsers,
      color: 'bg-gradient-to-r from-[#1e293b] to-blue-500',
      bgColor: 'bg-blue-50',
      items: [
        { label: 'Active', count: subscriptionActive.length, filter: 'enabled', color: 'bg-gray-100 hover:bg-green-200' },
        { label: 'Inactive', count: subscriptionInactive.length, filter: 'disabled', color: 'bg-red-100 hover:bg-red-200' }
      ]
    },
    // {
    //   id: 'tfa',
    //   title: 'Two Factor Auth',
    //   icon: FaShieldAlt,
    //   total: totalUsers,
    //   color: 'bg-gradient-to-r from-[#1e293b] to-blue-500',
    //   bgColor: 'bg-blue-50',
    //   items: [
    //     { label: 'Enabled', count: tfaEnabled.length, filter: 'enabled', color: 'bg-gray-100 hover:bg-green-200' },
    //     { label: 'Disabled', count: tfaDisabled.length, filter: 'disabled', color: 'bg-red-100 hover:bg-red-200' }
    //   ]
    // },
    {
      id: 'fund-pass',
      title: 'Fund Passcode',
      icon: FaLock,
      total: totalUsers,
      color: 'bg-gradient-to-r from-[#1e293b] to-blue-500',
      bgColor: 'bg-blue-50',
      items: [
        { label: 'Enabled', count: fundPassEnabled.length, filter: 'enabled', color: 'bg-gray-100 hover:bg-green-200' },
        { label: 'Disabled', count: fundPassDisabled.length, filter: 'disabled', color: 'bg-red-100 hover:bg-red-200' }
      ]
    },
    {
      id: 'age-gender',
      title: 'Age & Gender',
      icon: FaChartBar,
      total: (() => {
        switch (selectedAgeRange) {
          case '0-18': return users0_18.length;
          case '19-28': return users19_28.length;
          case '29-39': return users29_39.length;
          case '40-50': return users40_50.length;
          case '51-60': return users51_60.length;
          case '61-70': return users61_70.length;
          case '71+': return users71plus.length;
          default: return users0_18.length;
        }
      })(),
      color: 'bg-gradient-to-r from-[#1e293b] to-blue-500',
      bgColor: 'bg-blue-50',
      items: [
        {
          label: 'Male', count: (() => {
            switch (selectedAgeRange) {
              case '0-18': return users0_18_male.length;
              case '19-28': return users19_28_male.length;
              case '29-39': return users29_39_male.length;
              case '40-50': return users40_50_male.length;
              case '51-60': return users51_60_male.length;
              case '61-70': return users61_70_male.length;
              case '71+': return users71plus_male.length;
              default: return users0_18_male.length;
            }
          })(), filter: 'male', color: 'bg-blue-100 hover:bg-blue-200'
        },
        {
          label: 'Female', count: (() => {
            switch (selectedAgeRange) {
              case '0-18': return users0_18_female.length;
              case '19-28': return users19_28_female.length;
              case '29-39': return users29_39_female.length;
              case '40-50': return users40_50_female.length;
              case '51-60': return users51_60_female.length;
              case '61-70': return users61_70_female.length;
              case '71+': return users71plus_female.length;
              default: return users0_18_female.length;
            }
          })(), filter: 'female', color: 'bg-pink-100 hover:bg-pink-200'
        }
      ]
    },
    {
      id: 'logged-in',
      title: 'Logged In Users',
      icon: FaSignInAlt,
      total: selectedLoginType === 'website' ? websiteLoggedInUsers.length : loggedInUsers.length,
      color: 'bg-gradient-to-r from-[#1e293b] to-blue-500',
      bgColor: 'bg-blue-50',
      items: [
        { label: 'Active', count: selectedLoginType === 'website' ? websiteLoggedInActive.length : loggedInActive.length, filter: 'active', color: 'bg-green-100 hover:bg-green-200' },
        { label: 'Inactive', count: selectedLoginType === 'website' ? websiteLoggedInInactive.length : loggedInInactive.length, filter: 'non-active', color: 'bg-red-100 hover:bg-red-200' }
      ]
    },
    {
      id: 'blocked',
      title: 'Blocked Users',
      icon: FaBan,
      total: blockedUsers.length,
      color: 'bg-gradient-to-r from-[#1e293b] to-blue-500',
      bgColor: 'bg-blue-50',
      items: [
        { label: 'Male', count: blockedUsers_male.length, filter: 'male', color: 'bg-blue-100 hover:bg-blue-200' },
        { label: 'Female', count: blockedUsers_female.length, filter: 'female', color: 'bg-pink-100 hover:bg-pink-200' }
      ]
    }
  ];

  return isLoading ? (
    <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="text-center">
        <Lottie animationData={loader} style={style} loop={true} width={20} />
        <p className="text-blue-600 font-semibold mt-4">Loading Dashboard...</p>
      </div>
    </div>
  ) : (
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      {/* Enhanced Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-blue-200 shadow-sm sticky top-0 z-10">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4 py-13">
            {/* <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                User Analytics Dashboard
              </h1>
              <p className="text-gray-600 mt-1">Comprehensive user management and analytics</p>
            </div> */}

            {/* Enhanced Filters */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm border border-blue-200">
                <label className="text-gray-700 font-medium text-sm">Country</label>
                <select
                  value={countryFilter}
                  onChange={e => setCountryFilter(e.target.value)}
                  className="border-0 bg-transparent text-blue-600 font-semibold focus:outline-none focus:ring-0"
                >
                  {countryList.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm border border-blue-200">
                <label className="text-gray-700 font-medium text-sm">Date</label>
                <input
                  type="date"
                  value={dateFilter}
                  onChange={e => { setDateFilter(e.target.value); setAllDates(false); }}
                  className="border-0 bg-transparent text-blue-600 font-semibold focus:outline-none focus:ring-0"
                  disabled={allDates}
                />
                <label className="flex items-center gap-1 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allDates}
                    onChange={e => setAllDates(e.target.checked)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  All Dates
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Stat Cards Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {cardConfigs.map((card) => (
            <div
              key={card.id}
              className={`relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100`}
            >
              {/* Gradient Header */}
              <div className={`bg-gradient-to-r ${card.color} p-4 text-white`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium opacity-90">{card.title}</p>
                    <p className="text-2xl font-bold">{card.total.toLocaleString()}</p>
                  </div>
                  <div className={`p-3 rounded-full ${card.bgColor} bg-white/20 backdrop-blur-sm`}>
                    <card.icon className="text-2xl" />
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-2">
                  {card.items.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleCardClick(card.id, item.filter)}
                      className={`${item.color} rounded-lg p-3 text-center transition-all duration-200 transform hover:scale-105 ${selectedCard === card.id && selectedFilter === item.filter
                        ? 'ring-2 ring-blue-400 shadow-lg'
                        : ''
                        }`}
                    >
                      <div className="text-xs font-medium text-gray-600 mb-1">{item.label}</div>
                      <div className="text-lg font-bold text-gray-800">{item.count}</div>
                    </button>
                  ))}
                </div>

                {/* Special controls for age/gender and login type cards */}
                {card.id === 'age-gender' && (
                  <div className="mt-3">
                    <select
                      value={selectedAgeRange}
                      onChange={(e) => setSelectedAgeRange(e.target.value)}
                      className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="0-18">0-18 years</option>
                      <option value="19-28">19-28 years</option>
                      <option value="29-39">29-39 years</option>
                      <option value="40-50">40-50 years</option>
                      <option value="51-60">51-60 years</option>
                      <option value="61-70">61-70 years</option>
                      <option value="71+">71+ years</option>
                    </select>
                  </div>
                )}

                {card.id === 'logged-in' && (
                  <div className="mt-3">
                    <select
                      value={selectedLoginType}
                      onChange={(e) => setSelectedLoginType(e.target.value)}
                      className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="mobile">Mobile App</option>
                      <option value="website">Website</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Enhanced Graphs Section */}
        {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <Graph
              type="monthly"
              data={monthlyGraphData}
              selectedYear={selectedYear}
              years={years}
              onYearChange={setSelectedYear}
              title="Monthly User Registrations"
              subtitle={`Year: ${selectedYear}`}
            />
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <Graph
              type="daily"
              data={dailyGraphData}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
              years={years}
              months={months}
              onYearChange={setSelectedYear}
              onMonthChange={setSelectedMonth}
              title="Daily User Registrations"
              subtitle={`Month: ${months[selectedMonth-1]}, Year: ${selectedYear}`}
            />
          </div>
        </div> */}

        {/* Enhanced Users List Section */}
        <div ref={userListRef} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-[#1e293b] to-blue-600 px-6 py-4">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-xl font-bold text-white">Users List</h2>
                  {isAdmin() ? (
                    <span className="bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      👑 Admin - Full Access
                    </span>
                  ) : canWriteUserDetails ? (
                    <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      ✏️ Can Approve/Reject
                    </span>
                  ) : canReadUserDetails ? (
                    <span className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      👁️ Read Only
                    </span>
                  ) : (
                    <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      🚫 No Access
                    </span>
                  )}
                </div>
                <p className="text-blue-100 text-sm">
                  {selectedCard && selectedFilter ?
                    `Showing ${filteredData().length} filtered users` :
                    `Total ${totalUsers.toLocaleString()} users`}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                {/* Enhanced Search */}
                <div className="relative w-full sm:w-80">
                  <input
                    type="text"
                    placeholder="Search users by email, username, or UID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 pl-10 bg-white/90 backdrop-blur-sm border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 text-gray-800 placeholder-gray-500"
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 bg-white/50 rounded-full p-1"
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Enhanced Export */}
                <div className="flex flex-col sm:flex-row gap-2 items-center">
                  <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2">
                    <input
                      type="date"
                      value={exportStartDate}
                      onChange={e => setExportStartDate(e.target.value)}
                      className="border-0 bg-transparent text-gray-700 text-sm focus:outline-none focus:ring-0"
                      placeholder="Start Date"
                    />
                    <span className="text-black font-medium">to</span>
                    <input
                      type="date"
                      value={exportEndDate}
                      onChange={e => setExportEndDate(e.target.value)}
                      className="border-0 bg-transparent text-gray-700 text-sm focus:outline-none focus:ring-0"
                      placeholder="End Date"
                    />
                  </div>
                  <button
                    className="bg-white/90 backdrop-blur-sm hover:bg-white text-blue-600 px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 font-semibold shadow-sm hover:shadow-md"
                    onClick={exportFile}
                  >
                    <BiExport size={18} />
                    Export
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="h-[500px] w-full">
              <DataGrid
                ref={targetElement}
                rows={rows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10]}
                checkboxSelection
                disableSelectionOnClick
                className="border-0"
                sx={{
                  '& .MuiDataGrid-cell': {
                    fontSize: '0.875rem',
                    borderBottom: '1px solid #f1f5f9',
                    '@media (min-width: 640px)': {
                      fontSize: '1rem',
                    },
                  },
                  '& .MuiDataGrid-columnHeader': {
                    fontSize: '0.875rem',
                    backgroundColor: '#f8fafc',
                    color: '#1e293b',
                    fontWeight: '600',
                    borderBottom: '2px solid #e2e8f0',
                    '@media (min-width: 640px)': {
                      fontSize: '1rem',
                    },
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#f1f5f9',
                  },
                  '& .MuiDataGrid-row.Mui-selected': {
                    backgroundColor: '#dbeafe',
                  },
                  '& .MuiDataGrid-row.Mui-selected:hover': {
                    backgroundColor: '#bfdbfe',
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blocks;
